import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate, Link } from "react-router-dom";
import { useCustomerAuth, customerAxios } from "@/context/CustomerAuthContext";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingCart, FileText, Receipt, LifeBuoy, Bell, User, LogOut,
  ShieldCheck, Printer, Plus, Send, Check, MessageSquare,
} from "lucide-react";

function Protected({ children }) {
  const { user } = useCustomerAuth();
  if (user === undefined) return <div className="pt-24 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  return children;
}

const navItems = [
  { to: "/portal", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/orders", label: "Orders", icon: ShoppingCart },
  { to: "/portal/quotes", label: "Quotes", icon: FileText },
  { to: "/portal/invoices", label: "Invoices", icon: Receipt },
  { to: "/portal/tickets", label: "Support Tickets", icon: LifeBuoy },
  { to: "/portal/notifications", label: "Notifications", icon: Bell },
  { to: "/portal/profile", label: "Profile", icon: User },
];

function Shell({ children }) {
  const { user, logout } = useCustomerAuth();
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    customerAxios.get("/customer/dashboard").then(r => setUnread(r.data.unread_notifications || 0)).catch(()=>{});
  }, []);
  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/portal/login");
  };
  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-[260px_1fr]">
      <aside className="border-r border-black/5 dark:border-white/10 bg-white dark:bg-[#0d1a35]/40 p-4 md:sticky md:top-16 md:h-[calc(100vh-4rem)]">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-semibold tracking-tight">Client Portal</div>
            <div className="text-xs text-muted-foreground truncate max-w-[160px]">{user?.email}</div>
          </div>
        </div>
        <nav className="space-y-1">
          {navItems.map((i) => (
            <NavLink
              key={i.to} to={i.to} end={i.end}
              data-testid={`portal-nav-${i.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition ${
                  isActive ? "bg-[#1565FF] text-white" : "text-foreground/80 hover:bg-black/5 dark:hover:bg-white/5"
                }`
              }
            >
              <span className="flex items-center gap-2"><i.icon className="h-4 w-4" /> {i.label}</span>
              {i.label === "Notifications" && unread > 0 && (
                <span className="text-[10px] rounded-full bg-white/20 px-1.5 py-0.5">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 pt-4 border-t border-black/5 dark:border-white/10 space-y-1">
          <Link to="/portal/change-password" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground/80 hover:bg-black/5 dark:hover:bg-white/5" data-testid="portal-nav-change-password">
            <User className="h-4 w-4"/> Change Password
          </Link>
          <button onClick={onLogout} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10" data-testid="portal-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="p-6 md:p-10">{children}</main>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>;

function StatCard({ label, value, hint }) {
  return (
    <div className="card-elevated p-6">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tighter">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Dashboard() {
  const { user } = useCustomerAuth();
  const [d, setD] = useState(null);
  useEffect(() => { customerAxios.get("/customer/dashboard").then((r) => setD(r.data)); }, []);
  return (
    <>
      <SEO title="Client Dashboard — Tech Alpha SSL" />
      <h1 className="text-2xl font-semibold tracking-tight">Welcome back, {user?.name || "there"}.</h1>
      <p className="text-sm text-muted-foreground mt-1">Here's what's happening across your account.</p>
      <div className="mt-8 grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Orders" value={d?.orders ?? "—"} />
        <StatCard label="Quotes" value={d?.quotes ?? "—"} />
        <StatCard label="Invoices" value={d ? `$${d.invoices_total.toFixed(2)}` : "—"} hint="Billed + Paid" />
        <StatCard label="Open Tickets" value={d?.open_tickets ?? "—"} />
        <StatCard label="Unread" value={d?.unread_notifications ?? "—"} />
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <Link to="/portal/tickets" className="card-elevated p-6 hover:-translate-y-1 transition" data-testid="portal-cta-tickets">
          <LifeBuoy className="h-6 w-6 text-[#1565FF] dark:text-[#4FC3F7]"/>
          <h3 className="mt-3 text-base font-semibold">Need help?</h3>
          <p className="text-sm text-muted-foreground">Open a support ticket. We respond within one business hour.</p>
        </Link>
        <Link to="/pricing" className="card-elevated p-6 hover:-translate-y-1 transition" data-testid="portal-cta-pricing">
          <ShoppingCart className="h-6 w-6 text-[#1565FF] dark:text-[#4FC3F7]"/>
          <h3 className="mt-3 text-base font-semibold">Explore services</h3>
          <p className="text-sm text-muted-foreground">Upgrade your plan or add a new service.</p>
        </Link>
      </div>
    </>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  useEffect(() => { customerAxios.get("/customer/orders").then(r => setRows(r.data)); }, []);
  return (
    <>
      <SEO title="Orders — Client Portal" />
      <h1 className="text-2xl font-semibold tracking-tight">Orders</h1>
      <p className="text-sm text-muted-foreground mt-1">Purchases and payment intents.</p>
      <div className="mt-6 card-elevated overflow-hidden" data-testid="portal-orders-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><Th>Plan</Th><Th>Price</Th><Th>Placed</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10">
                <Td>{r.plan_name}</Td>
                <Td>${Number(r.price || 0).toFixed(2)}</Td>
                <Td className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><Td colSpan={3} className="text-center text-muted-foreground py-8">No orders yet. <Link to="/pricing" className="text-[#1565FF]">Browse plans</Link>.</Td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function Quotes() {
  const [rows, setRows] = useState([]);
  useEffect(() => { customerAxios.get("/customer/quotes").then(r => setRows(r.data)); }, []);
  return (
    <>
      <SEO title="Quotes — Client Portal" />
      <h1 className="text-2xl font-semibold tracking-tight">Quotes</h1>
      <p className="text-sm text-muted-foreground mt-1">All quote requests linked to your email.</p>
      <div className="mt-6 card-elevated overflow-hidden" data-testid="portal-quotes-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><Th>Service</Th><Th>Budget</Th><Th>Timeline</Th><Th>Status</Th><Th>Submitted</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10 align-top">
                <Td>{r.service}</Td>
                <Td>{r.budget || "—"}</Td>
                <Td>{r.timeline || "—"}</Td>
                <Td><span className="text-xs rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-1">{r.status || "pending"}</span></Td>
                <Td className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><Td colSpan={5} className="text-center text-muted-foreground py-8">No quotes yet. <Link to="/quote" className="text-[#1565FF]">Request one</Link>.</Td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function Invoices() {
  const [rows, setRows] = useState([]);
  useEffect(() => { customerAxios.get("/customer/invoices").then(r => setRows(r.data)); }, []);
  return (
    <>
      <SEO title="Invoices — Client Portal" />
      <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
      <p className="text-sm text-muted-foreground mt-1">Download or print any invoice.</p>
      <div className="mt-6 card-elevated overflow-hidden" data-testid="portal-invoices-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><Th>Number</Th><Th>Total</Th><Th>Status</Th><Th>Issued</Th><Th>Due</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10">
                <Td className="font-medium">{r.number}</Td>
                <Td>${Number(r.total || 0).toFixed(2)}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td className="text-muted-foreground text-xs">{new Date(r.created_at).toLocaleDateString()}</Td>
                <Td className="text-muted-foreground text-xs">{r.due_date || "—"}</Td>
                <Td>
                  <Link to={`/portal/invoices/${r.id}`} className="text-[#1565FF] text-sm inline-flex items-center gap-1" data-testid={`portal-invoice-view-${r.id}`}>
                    <Printer className="h-3.5 w-3.5"/> View
                  </Link>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><Td colSpan={6} className="text-center text-muted-foreground py-8">No invoices yet.</Td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-500/10 text-slate-500",
    sent: "bg-[#1565FF]/10 text-[#1565FF]",
    paid: "bg-emerald-500/10 text-emerald-600",
    overdue: "bg-red-500/10 text-red-600",
    cancelled: "bg-slate-400/10 text-slate-400",
    open: "bg-[#1565FF]/10 text-[#1565FF]",
    answered: "bg-emerald-500/10 text-emerald-600",
    closed: "bg-slate-500/10 text-slate-500",
  };
  return <span className={`text-xs rounded-full px-2 py-1 ${map[status] || "bg-slate-500/10 text-slate-500"}`}>{status}</span>;
}

function InvoiceDetail() {
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const id = window.location.pathname.split("/").pop();
  useEffect(() => {
    customerAxios.get(`/customer/invoices/${id}`)
      .then(r => setInv(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Not found"));
  }, [id]);
  if (err) return <p className="text-muted-foreground">Invoice not found.</p>;
  if (!inv) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <>
      <SEO title={`Invoice ${inv.number} — Client Portal`} />
      <div className="flex items-center justify-between print:hidden">
        <div>
          <Link to="/portal/invoices" className="text-sm text-muted-foreground hover:text-[#1565FF]">← Back to invoices</Link>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">Invoice {inv.number}</h1>
        </div>
        <button onClick={() => window.print()} className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-invoice-print">
          <Printer className="h-4 w-4"/> Download / Print
        </button>
      </div>

      <div id="invoice-print" className="mt-8 card-elevated p-10 max-w-3xl print:shadow-none print:border-0">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <span className="font-semibold tracking-tight">Tech Alpha SSL</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">techalphassl.com<br/>techalphassl@gmail.com</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Invoice</div>
            <div className="text-2xl font-semibold tracking-tighter">{inv.number}</div>
            <div className="mt-1 text-xs text-muted-foreground">Issued: {new Date(inv.created_at).toLocaleDateString()}</div>
            {inv.due_date && <div className="text-xs text-muted-foreground">Due: {inv.due_date}</div>}
            <div className="mt-2"><StatusBadge status={inv.status}/></div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Bill to</div>
            <div className="mt-1 font-medium">{inv.customer_name}</div>
            <div className="text-sm text-muted-foreground">{inv.customer_email}</div>
          </div>
        </div>

        <table className="w-full text-sm mt-8">
          <thead className="text-left border-b border-black/10 dark:border-white/10">
            <tr><Th>Description</Th><Th>Qty</Th><Th>Unit</Th><Th className="text-right">Amount</Th></tr>
          </thead>
          <tbody>
            {(inv.items || []).map((it, i) => (
              <tr key={i} className="border-b border-black/5 dark:border-white/10">
                <Td>{it.description}</Td>
                <Td>{it.quantity}</Td>
                <Td>${Number(it.unit_price || 0).toFixed(2)}</Td>
                <Td className="text-right">${(it.quantity * it.unit_price).toFixed(2)}</Td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${Number(inv.subtotal || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({inv.tax_rate}%)</span><span>${Number(inv.tax || 0).toFixed(2)}</span></div>
            <div className="pt-2 mt-2 border-t border-black/10 dark:border-white/10 flex justify-between font-semibold text-base">
              <span>Total</span><span>${Number(inv.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {inv.notes && (
          <div className="mt-8 p-4 bg-black/[0.02] dark:bg-white/[0.02] rounded-lg text-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Notes</div>
            <p className="mt-1 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}

        <p className="mt-8 text-xs text-muted-foreground text-center">Thank you for your business.</p>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #invoice-print, #invoice-print * { visibility: visible !important; }
          #invoice-print { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </>
  );
}

function Tickets() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal" });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => customerAxios.get("/customer/tickets").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await customerAxios.post("/customer/tickets", form);
      toast.success("Ticket opened.");
      setOpen(false); setForm({ subject: "", message: "", priority: "normal" });
      load();
    } catch { toast.error("Failed to open ticket"); }
  };

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    try {
      await customerAxios.post(`/customer/tickets/${id}/reply`, { message: reply });
      setReply("");
      const r = await customerAxios.get("/customer/tickets");
      setRows(r.data);
      const cur = r.data.find(t => t.id === id);
      setSelected(cur || null);
    } catch { toast.error("Failed to send"); }
  };

  return (
    <>
      <SEO title="Support Tickets — Client Portal" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Support Tickets</h1>
          <p className="text-sm text-muted-foreground mt-1">Direct line to our engineering team.</p>
        </div>
        <button onClick={() => setOpen(v => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-new-ticket">
          <Plus className="h-4 w-4"/> New ticket
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-6 card-elevated p-6 space-y-3" data-testid="portal-new-ticket-form">
          <input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="portal-ticket-subject"
            className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} data-testid="portal-ticket-priority"
            className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm">
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
          </select>
          <textarea required rows={4} placeholder="Describe the issue" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="portal-ticket-message"
            className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          <button className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-ticket-submit"><Send className="h-4 w-4"/> Submit</button>
        </form>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-[380px_1fr]">
        <div className="card-elevated divide-y divide-black/5 dark:divide-white/10 max-h-[70vh] overflow-y-auto">
          {rows.map(t => (
            <button key={t.id} onClick={() => setSelected(t)} data-testid={`portal-ticket-item-${t.id}`}
              className={`w-full text-left p-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition ${selected?.id === t.id ? "bg-black/[0.03] dark:bg-white/[0.03]" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium tracking-tight truncate">{t.subject}</div>
                <StatusBadge status={t.status} />
              </div>
              <div className="text-xs text-muted-foreground mt-1">Priority: {t.priority} · Updated {new Date(t.updated_at).toLocaleString()}</div>
            </button>
          ))}
          {rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No tickets yet.</p>}
        </div>

        <div className="card-elevated p-6">
          {!selected ? (
            <p className="text-muted-foreground text-sm">Select a ticket to view the conversation.</p>
          ) : (
            <div className="flex flex-col h-full">
              <div>
                <div className="text-lg font-semibold tracking-tight">{selected.subject}</div>
                <div className="text-xs text-muted-foreground">Ticket · {selected.priority} priority · <StatusBadge status={selected.status}/></div>
              </div>
              <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-2">
                {(selected.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.author === "admin" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.author === "admin" ? "bg-[#1565FF]/10 text-foreground" : "bg-[#1565FF] text-white"}`}>
                      <div className="text-[10px] opacity-70 mb-1">{m.name || m.author} · {new Date(m.at).toLocaleString()}</div>
                      <div className="whitespace-pre-wrap">{m.message}</div>
                    </div>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="mt-4 pt-4 border-t border-black/5 dark:border-white/10 flex gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" data-testid="portal-ticket-reply-input"
                    className="flex-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 text-sm" />
                  <button onClick={() => sendReply(selected.id)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-ticket-reply-send">
                    <Send className="h-3.5 w-3.5"/> Send
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Notifications() {
  const [rows, setRows] = useState([]);
  const load = () => customerAxios.get("/customer/notifications").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const markAll = async () => { await customerAxios.post("/customer/notifications/read-all"); load(); };
  const markOne = async (id) => { await customerAxios.post(`/customer/notifications/${id}/read`); load(); };
  return (
    <>
      <SEO title="Notifications — Client Portal" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">System updates and support activity.</p>
        </div>
        <button onClick={markAll} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="portal-mark-all-read"><Check className="h-4 w-4"/> Mark all read</button>
      </div>
      <div className="mt-6 card-elevated divide-y divide-black/5 dark:divide-white/10" data-testid="portal-notifications-list">
        {rows.map(n => (
          <div key={n.id} className={`p-4 flex items-start justify-between gap-4 ${!n.read ? "bg-[#1565FF]/[0.03]" : ""}`}>
            <div>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-[#1565FF] dark:text-[#4FC3F7]" />
                <span className="text-sm font-medium tracking-tight">{n.title}</span>
                {!n.read && <span className="text-[10px] rounded-full bg-[#1565FF] text-white px-2 py-0.5">New</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
              <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
            </div>
            {!n.read && (
              <button onClick={() => markOne(n.id)} className="text-xs text-[#1565FF] whitespace-nowrap" data-testid={`portal-mark-read-${n.id}`}>Mark read</button>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</p>}
      </div>
    </>
  );
}

function Profile() {
  const { user, refresh } = useCustomerAuth();
  const [form, setForm] = useState({ name: user?.name || "", company: user?.company || "", phone: user?.phone || "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { setForm({ name: user?.name || "", company: user?.company || "", phone: user?.phone || "" }); }, [user]);
  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await customerAxios.patch("/customer/profile", form);
      await refresh();
      toast.success("Profile updated.");
    } catch { toast.error("Failed to update"); }
    finally { setLoading(false); }
  };
  return (
    <>
      <SEO title="Profile — Client Portal" />
      <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
      <p className="text-sm text-muted-foreground mt-1">Keep your account details current.</p>
      <form onSubmit={save} className="mt-6 card-elevated p-8 max-w-xl space-y-3" data-testid="portal-profile-form">
        <div>
          <label className="text-sm font-medium">Email</label>
          <input value={user?.email || ""} disabled className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 px-4 py-3 text-sm text-muted-foreground" />
        </div>
        <div>
          <label className="text-sm font-medium">Full name</label>
          <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="portal-profile-name"
            className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Company</label>
          <input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} data-testid="portal-profile-company"
            className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Phone</label>
          <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="portal-profile-phone"
            className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        </div>
        <button disabled={loading} className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-profile-save">Save changes</button>
      </form>
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmVal, setConfirmVal] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirmVal) return toast.error("Passwords do not match");
    if (next.length < 8) return toast.error("Password must be at least 8 characters");
    setLoading(true);
    try {
      await customerAxios.post("/customer/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      nav("/portal");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <>
      <SEO title="Change Password — Client Portal" />
      <h1 className="text-2xl font-semibold tracking-tight">Change password</h1>
      <form onSubmit={submit} className="mt-6 card-elevated p-8 max-w-xl space-y-3" data-testid="portal-change-password-form">
        <input type="password" required placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="portal-cp-current"
          className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        <input type="password" required placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="portal-cp-new"
          className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        <input type="password" required placeholder="Confirm new password" value={confirmVal} onChange={(e) => setConfirmVal(e.target.value)} data-testid="portal-cp-confirm"
          className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm" />
        <button disabled={loading} className="btn-primary !py-2 !px-4 !text-sm" data-testid="portal-cp-submit">Update password</button>
      </form>
    </>
  );
}

export default function PortalApp() {
  return (
    <Protected>
      <Shell>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="orders" element={<Orders />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="profile" element={<Profile />} />
          <Route path="change-password" element={<ChangePassword />} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </Shell>
    </Protected>
  );
}
